import type { TweetLinkMetadata } from '@sweetistics/contracts/tweets-canonical';
import type {
  ImageHashValue,
  LinkDescriptionSource,
  LinkMetadata,
  LinkMetadataDetails,
  LinkMetadataLLMDetails,
} from '@/lib/fetch-metadata';
import type { AICostSummary, AIUsageSummary } from '@/types/ai';

const LEGACY_HASH_ALGO = 'legacy-string';

const isObjectLike = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;

const coerceImageHash = (value: unknown): ImageHashValue | null => {
  if (value == null) {
    return null;
  }

  if (typeof value === 'string' && value.trim().length > 0) {
    return { algo: LEGACY_HASH_ALGO, value: value.trim() } satisfies ImageHashValue;
  }

  if (!isObjectLike(value)) {
    return null;
  }

  const { algo, value: hashValue } = value as { algo?: unknown; value?: unknown };
  if (typeof algo === 'string' && typeof hashValue === 'string') {
    return { algo, value: hashValue } satisfies ImageHashValue;
  }

  return null;
};

const coerceEmbeddableStatus = (value: unknown): LinkMetadata['embeddableStatus'] =>
  value === 'allow' || value === 'deny' || value === 'unknown' ? value : 'unknown';

const asOptionalString = (value: unknown): string | undefined => (typeof value === 'string' ? value : undefined);

const asOptionalNullableString = (value: unknown): string | null | undefined => {
  if (value === null) {
    return null;
  }
  return asOptionalString(value);
};

const coerceUsageSummary = (value: unknown): AIUsageSummary | null => {
  if (!isObjectLike(value)) {
    return null;
  }

  const { promptTokens, completionTokens, totalTokens, reasoningTokens } = value;
  if (typeof promptTokens !== 'number' || typeof completionTokens !== 'number' || typeof totalTokens !== 'number') {
    return null;
  }

  const summary: AIUsageSummary = {
    promptTokens,
    completionTokens,
    totalTokens,
  };

  if (typeof reasoningTokens === 'number') {
    summary.reasoningTokens = reasoningTokens;
  }

  return summary;
};

const coerceCostSummary = (value: unknown): AICostSummary | null => {
  if (!isObjectLike(value)) {
    return null;
  }

  const { inputCostCents, outputCostCents, totalCostCents, pricingModel } = value;
  if (
    typeof inputCostCents !== 'number' ||
    typeof outputCostCents !== 'number' ||
    typeof totalCostCents !== 'number' ||
    typeof pricingModel !== 'string'
  ) {
    return null;
  }

  return {
    inputCostCents,
    outputCostCents,
    totalCostCents,
    pricingModel,
  };
};

const coerceLinkMetadataLLMDetails = (value: unknown): LinkMetadataLLMDetails | null | undefined => {
  if (value === undefined) {
    return;
  }
  if (value === null) {
    return null;
  }
  if (!isObjectLike(value)) {
    return null;
  }

  const record = value as RawMetadataDetails;
  const model = asOptionalString(record.model);
  const providerModelId = asOptionalString(record.providerModelId);
  const statusCandidate = record.status;
  const status: LinkMetadataLLMDetails['status'] | null =
    statusCandidate === 'success' || statusCandidate === 'failed' ? statusCandidate : null;
  if (!(model && providerModelId && status)) {
    return null;
  }

  const llmDetails: LinkMetadataLLMDetails = {
    model,
    providerModelId,
    status,
    usage: coerceUsageSummary(record.usage),
    cost: coerceCostSummary(record.cost),
    enrichedAt: asOptionalNullableString(record.enrichedAt) ?? null,
  };

  const latency = record.latencyMs;
  if (typeof latency === 'number' || latency === null) {
    llmDetails.latencyMs = latency;
  }

  const requestId = asOptionalNullableString(record.requestId);
  if (requestId !== undefined) {
    llmDetails.requestId = requestId;
  }

  const error = asOptionalString(record.error);
  if (error !== undefined) {
    llmDetails.error = error;
  }

  return llmDetails;
};

const coerceDescriptionSource = (value: unknown): LinkDescriptionSource =>
  value === 'fallback' || value === 'llm' ? value : 'meta';

const coerceLinkMetadataDetails = (value: unknown): LinkMetadataDetails | null | undefined => {
  if (value === undefined) {
    return;
  }
  if (value === null) {
    return null;
  }
  if (!isObjectLike(value)) {
    return null;
  }

  const record = value as RawMetadataDetails;

  const details: LinkMetadataDetails = {
    descriptionSource: coerceDescriptionSource(record.descriptionSource),
  };

  const llmDetails = coerceLinkMetadataLLMDetails(record.llm);
  if (llmDetails !== undefined) {
    details.llm = llmDetails;
  }

  const articleMarkdown = asOptionalNullableString(record.articleMarkdown);
  if (articleMarkdown !== undefined) {
    details.articleMarkdown = articleMarkdown;
  }

  return details;
};

const assignOptionalStrings = (
  source: Record<string, unknown>,
  target: LinkMetadata,
  fields: readonly string[]
): void => {
  for (const field of fields) {
    const coerced = asOptionalString(source[field]);
    if (coerced !== undefined) {
      target[field] = coerced;
    }
  }
};

type RawLinkMetadataRecord = {
  url?: unknown;
  embeddableStatus?: unknown;
  imageHash?: unknown;
  metadata?: unknown;
  error?: unknown;
  articleMarkdown?: unknown;
  descriptionSource?: unknown;
  llm?: unknown;
};

type RawMetadataDetails = {
  model?: unknown;
  providerModelId?: unknown;
  status?: unknown;
  usage?: unknown;
  cost?: unknown;
  enrichedAt?: unknown;
  latencyMs?: unknown;
  requestId?: unknown;
  stage?: unknown;
  message?: unknown;
  error?: unknown;
  descriptionSource?: unknown;
  llm?: unknown;
  articleMarkdown?: unknown;
};

const assignNullableStrings = (
  source: Record<string, unknown>,
  target: LinkMetadata,
  fields: readonly string[]
): void => {
  for (const field of fields) {
    const coerced = asOptionalNullableString(source[field]);
    if (coerced !== undefined) {
      target[field] = coerced;
    }
  }
};

export const normalizeLinkMetadata = (value: unknown, fallbackUrl?: string): LinkMetadata | null => {
  if (!isObjectLike(value)) {
    return null;
  }

  const record = value as RawLinkMetadataRecord;
  const recordFields = record as Record<string, unknown>;
  const rawUrl = record.url;
  const url = typeof rawUrl === 'string' && rawUrl.trim().length > 0 ? rawUrl.trim() : fallbackUrl;
  if (!url) {
    return null;
  }

  const metadata: LinkMetadata = {
    url,
    embeddableStatus: coerceEmbeddableStatus(record.embeddableStatus),
  };

  assignOptionalStrings(recordFields, metadata, ['title', 'description', 'image', 'siteName', 'video']);
  assignNullableStrings(recordFields, metadata, ['favicon']);

  if (record.imageHash === null) {
    metadata.imageHash = null;
  } else {
    const imageHash = coerceImageHash(record.imageHash);
    if (imageHash) {
      metadata.imageHash = imageHash;
    }
  }

  if ('metadata' in record) {
    const metadataDetails = coerceLinkMetadataDetails(record.metadata);
    if (metadataDetails !== undefined) {
      metadata.metadata = metadataDetails ?? null;
    }
  }

  if ('error' in record) {
    const rawError = record.error;
    if (isLinkMetadataError(rawError)) {
      metadata.error = rawError;
    }
  }

  if ('articleMarkdown' in record) {
    const articleMarkdown = asOptionalNullableString(record.articleMarkdown);
    if (articleMarkdown !== undefined) {
      metadata.articleMarkdown = articleMarkdown;
    }
  }

  return metadata;
};

export const normalizeLinkMetadataRecord = (value: unknown): Record<string, LinkMetadata> | null => {
  if (!isObjectLike(value)) {
    return null;
  }

  const record: Record<string, LinkMetadata> = {};

  for (const [url, raw] of Object.entries(value)) {
    const metadata = normalizeLinkMetadata(raw, url);
    if (!metadata) {
      continue;
    }
    record[url] = metadata;
  }

  return Object.keys(record).length > 0 ? record : null;
};

export const fromCanonicalLinkMetadataRecord = (value: unknown): Record<string, LinkMetadata> | undefined => {
  const normalized = normalizeLinkMetadataRecord(value);
  return normalized ?? undefined;
};

export const toLinkMetadataRecord = (value: unknown): Record<string, LinkMetadata> | undefined => {
  const normalized = normalizeLinkMetadataRecord(value);
  return normalized ?? undefined;
};

export const toCanonicalLinkMetadataRecord = (value: unknown): Record<string, TweetLinkMetadata> | undefined => {
  const normalized = toLinkMetadataRecord(value);
  if (!normalized) {
    return;
  }

  const canonicalEntries: [string, TweetLinkMetadata][] = [];

  for (const [url, meta] of Object.entries(normalized)) {
    const imageHashValue = meta.imageHash;
    let canonicalImageHash: ImageHashValue | null | undefined;
    if (imageHashValue === null) {
      canonicalImageHash = null;
    } else if (
      isObjectLike(imageHashValue) &&
      typeof imageHashValue.algo === 'string' &&
      typeof imageHashValue.value === 'string'
    ) {
      canonicalImageHash = { algo: imageHashValue.algo, value: imageHashValue.value };
    } else {
      canonicalImageHash = undefined;
    }

    const faviconValue = asOptionalNullableString(meta.favicon);
    const metadataValue = meta.metadata === undefined ? undefined : coerceLinkMetadataDetails(meta.metadata);
    const errorValue = meta.error ?? undefined;
    let articleMarkdownValue: string | null | undefined;
    if (meta.articleMarkdown === null) {
      articleMarkdownValue = null;
    } else if (typeof meta.articleMarkdown === 'string' && meta.articleMarkdown.length > 0) {
      articleMarkdownValue = meta.articleMarkdown;
    } else {
      articleMarkdownValue = undefined;
    }

    canonicalEntries.push([
      url,
      {
        title: typeof meta.title === 'string' && meta.title.length > 0 ? meta.title : undefined,
        description: typeof meta.description === 'string' && meta.description.length > 0 ? meta.description : undefined,
        image: typeof meta.image === 'string' && meta.image.length > 0 ? meta.image : undefined,
        imageHash: canonicalImageHash,
        favicon: faviconValue,
        siteName: typeof meta.siteName === 'string' && meta.siteName.length > 0 ? meta.siteName : undefined,
        url: typeof meta.url === 'string' ? meta.url : url,
        video: typeof meta.video === 'string' && meta.video.length > 0 ? meta.video : undefined,
        embeddableStatus: meta.embeddableStatus,
        metadata: metadataValue,
        error: errorValue,
        articleMarkdown: articleMarkdownValue,
      },
    ]);
  }

  return canonicalEntries.length > 0 ? Object.fromEntries(canonicalEntries) : undefined;
};

export const isLinkMetadata = (value: unknown): value is LinkMetadata => normalizeLinkMetadata(value) !== null;

const isLinkMetadataError = (value: unknown): value is LinkMetadata['error'] => {
  if (!isObjectLike(value)) {
    return false;
  }
  const record = value as RawMetadataErrorRecord;
  return typeof record.stage === 'string' && typeof record.message === 'string';
};
type RawMetadataErrorRecord = {
  stage?: unknown;
  message?: unknown;
};
